<template>
  <div>
    <v-overlay color='#fff' :value='loading' :absolute='true'>
      <Cog v-if='loading' />
    </v-overlay>
    <div :id='id'></div>
  </div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';
import Cog from '@/components/animated/cog.vue';

am4core.addLicense('CH315878972');

/* Chart code */
am4core.options.queue = true;
// Themes begin
am4core.useTheme(am4themesAnimated);
// Themes end

export default {
  components: {
    Cog,
  },
  data() {
    return {
      chart: null,
      seriesRef: null,
      loading: true,
    };
  },
  props: {
    disposeChart: {
      type: Boolean,
    },
    id: {
      type: String,
      default: 'Log Chart',
    },
    title: {
      type: String,
      default: 'Log Chart',
    },
    colors: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Array,
      default: () => [
        {
          date: '2012-03-01',
          Pedestrian: 0.01,
          Cyclist: 2,
        },
        {
          date: '2012-03-02',
          Pedestrian: 75,
          Cyclist: 3,
        },
        {
          date: '2012-03-03',
          Pedestrian: 15,
          Cyclist: 10,
        },
        {
          date: '2012-03-04',
          Pedestrian: 75,
          Cyclist: 65,
        },
        {
          date: '2012-03-05',
          Pedestrian: 158,
          Cyclist: 57,
        },
        {
          date: '2012-03-06',
          Pedestrian: 57,
          Cyclist: 3,
        },
        {
          date: '2012-03-07',
          Pedestrian: 0.01,
          Cyclist: 65,
        },
        {
          date: '2012-03-08',
          Pedestrian: 89,
          Cyclist: 8,
        },
        {
          date: '2012-03-09',
          Pedestrian: 5,
          Cyclist: 75,
        },
        {
          date: '2012-03-10',
          Pedestrian: 67,
          Cyclist: 132,
        },
        {
          date: '2012-03-11',
          Pedestrian: 34,
          Cyclist: 380,
        },
        {
          date: '2012-03-12',
          Pedestrian: 80,
          Cyclist: 56,
        },
        {
          date: '2012-03-13',
          Pedestrian: 0.01,
          Cyclist: 0.01,
        },
        {
          date: '2012-03-14',
          Pedestrian: 24,
          Cyclist: 24,
        },
        {
          date: '2012-03-15',
          Pedestrian: 120,
          Cyclist: 147,
        },
      ],
    },
    yAxis: {
      type: Object,
      default: () => ({ label: 'Annual_Average_Daily_Traffic' }),
    },
    xAxis: {
      type: Object,
      default: () => ({ label: 'Site' }),
    },
    height: {
      type: String,
      default: () => '400px',
    },
    width: {
      type: String,
      default: () => '100%',
    },
    labels: {
      type: Function,
      default: () => false,
    },
  },
  mounted() {
    /* Chart code */
    // Create chart instance
    const chartContainer = document.getElementById(this.id);
    chartContainer.style.width = this.width;
    chartContainer.style.height = this.height;
    chartContainer.style.paddingBottom = '15px';
    chartContainer.style.paddingRight = '5px';
    chartContainer.style.paddingLeft = '5px';
    this.chart = am4core.create(this.id, am4charts.XYChart);
    // Add chart title
    const title = this.chart.titles.create();
    title.text = this.title;

    // Add data
    this.chart.data = this.data;
    this.chart.preloader.disabled = true;
    this.chart.events.on('ready', () => {
      this.loading = false;
    });
    this.chart.events.on('beforedatavalidated', () => {
      this.loading = true;
    });
    this.chart.events.on('datavalidated', () => {
      this.loading = false;
    });

    // Create axes
    const dateAxis = this.chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.renderer.grid.template.location = 0;
    dateAxis.renderer.minGridDistance = 50;
    dateAxis.title.text = 'ISODatetime';
    dateAxis.renderer.minGridDistance = 40;
    dateAxis.baseInterval = {
      timeUnit: 'hour',
      count: 1,
    };

    const valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.logarithmic = true;
    valueAxis.renderer.minGridDistance = 20;

    // Create series
    const series = this.chart.series.push(new am4charts.LineSeries());
    series.dataFields.valueY = 'Cyclist';
    series.dataFields.dateX = 'date';
    series.name = 'Cyclist';
    series.tensionX = 0.8;
    series.strokeWidth = 3;
    series.tooltipText = '{dateX}: {valueY}';
    series.fill = am4core.color(this.colors[0]);
    series.stroke = am4core.color(this.colors[0]);

    const series2 = this.chart.series.push(new am4charts.LineSeries());
    series2.dataFields.valueY = 'Pedestrian';
    series2.dataFields.dateX = 'date';
    series2.name = 'Pedestrian';
    series2.tensionX = 0.8;
    series2.strokeWidth = 3;
    series2.tooltipText = '{dateX}: {valueY}';
    series2.fill = am4core.color(this.colors[1]);
    series2.stroke = am4core.color(this.colors[1]);

    const series3 = this.chart.series.push(new am4charts.LineSeries());
    series3.dataFields.valueY = 'Bus';
    series3.dataFields.dateX = 'date';
    series3.name = 'Bus/Coach';
    series3.tensionX = 0.8;
    series3.strokeWidth = 3;
    series3.tooltipText = '{dateX}: {valueY}';
    series3.fill = am4core.color(this.colors[2]);
    series3.stroke = am4core.color(this.colors[2]);

    const series4 = this.chart.series.push(new am4charts.LineSeries());
    series4.dataFields.valueY = 'Car';
    series4.dataFields.dateX = 'date';
    series4.name = 'Car/Taxi';
    series4.tensionX = 0.8;
    series4.strokeWidth = 3;
    series4.tooltipText = '{dateX}: {valueY}';
    series4.fill = am4core.color(this.colors[3]);
    series4.stroke = am4core.color(this.colors[3]);

    const series5 = this.chart.series.push(new am4charts.LineSeries());
    series5.dataFields.valueY = 'Motorcycle';
    series5.dataFields.dateX = 'date';
    series5.name = 'Motorcycle';
    series5.tensionX = 0.8;
    series5.strokeWidth = 3;
    series5.tooltipText = '{dateX}: {valueY}';
    series5.fill = am4core.color(this.colors[4]);
    series5.stroke = am4core.color(this.colors[4]);

    const series6 = this.chart.series.push(new am4charts.LineSeries());
    series6.dataFields.valueY = 'Goods';
    series6.dataFields.dateX = 'date';
    series6.name = 'Goods Vehicle';
    series6.tensionX = 0.8;
    series6.strokeWidth = 3;
    series6.tooltipText = '{dateX}: {valueY}';
    series6.fill = am4core.color(this.colors[5]);
    series6.stroke = am4core.color(this.colors[5]);

    const bullet = series.bullets.push(new am4core.Circle());
    bullet.fill = am4core.color('#fff');
    bullet.radius = 6;
    const bullethover = bullet.states.create('hover');
    bullethover.properties.scale = 1.5;

    const bullet2 = series2.bullets.push(new am4core.Circle());
    bullet2.fill = am4core.color('#fff');
    bullet2.radius = 6;
    const bullethover2 = bullet2.states.create('hover');
    bullethover2.properties.scale = 1.5;

    const bullet3 = series3.bullets.push(new am4core.Circle());
    bullet3.fill = am4core.color('#fff');
    bullet3.radius = 6;
    const bullethover3 = bullet3.states.create('hover');
    bullethover3.properties.scale = 1.5;

    const bullet4 = series4.bullets.push(new am4core.Circle());
    bullet4.fill = am4core.color('#fff');
    bullet4.radius = 6;
    const bullethover4 = bullet4.states.create('hover');
    bullethover4.properties.scale = 1.5;

    const bullet5 = series5.bullets.push(new am4core.Circle());
    bullet5.fill = am4core.color('#fff');
    bullet5.radius = 6;
    const bullethover5 = bullet5.states.create('hover');
    bullethover5.properties.scale = 1.5;

    const bullet6 = series6.bullets.push(new am4core.Circle());
    bullet6.fill = am4core.color('#fff');
    bullet6.radius = 6;
    const bullethover6 = bullet5.states.create('hover');
    bullethover6.properties.scale = 1.5;

    // Add cursor
    this.chart.cursor = new am4charts.XYCursor();
    this.chart.cursor.fullWidthLineX = true;
    this.chart.cursor.xAxis = dateAxis;
    this.chart.cursor.lineX.strokeWidth = 0;
    this.chart.cursor.lineX.fill = am4core.color('#000');
    this.chart.cursor.lineX.fillOpacity = 0.1;

    // Add scrollbar
    this.chart.scrollbarX = new am4core.Scrollbar();

    this.chart.legend = new am4charts.Legend();
    this.chart.legend.useDefaultMarker = true;
    const marker = this.chart.legend.markers.template.children.getIndex(0);
    marker.cornerRadius(12, 12, 12, 12);
    marker.strokeWidth = 2;
    marker.strokeOpacity = 1;
    marker.stroke = am4core.color('#ccc');

    // Add a guide
    // const range = valueAxis.axisRanges.create();
    // range.value = 90.4;
    // range.grid.stroke = am4core.color('#396478');
    // range.grid.strokeWidth = 1;
    // range.grid.strokeOpacity = 1;
    // range.grid.strokeDasharray = '3,3';
    // range.label.inside = true;
    // range.label.text = 'Average';
    // range.label.fill = range.grid.stroke;
    // range.label.verticalCenter = 'bottom';
  },
  watch: {
    data(newValue) {
      if (newValue) {
        this.chart.data = newValue;
        this.loading = true;
      }
    },
    title(newValue) {
      if (newValue) {
        this.chart.titles.getIndex(0).text = newValue;
      }
    },
    disposeChart(newValue) {
      if (newValue) {
        this.chart.dispose();
      }
    },
  },
};
</script>

<style>
</style>
